import type { ReactNode } from 'react'
import { errorCopy, type ResourceState } from '../data/resource'

/**
 * One renderer for the four states, so no screen invents its own spinner and no
 * screen forgets one of them.
 *
 * The retry control is a real button with a label rather than an icon, and it sits
 * inside the panel where the missing content was - the thumb is already there,
 * and a failure that requires reaching for a header is a failure that gets left
 * alone. Loading is announced politely rather than assertively: a station operator
 * mid-scan should not have a screen reader interrupt them to say "loading".
 */
export interface AsyncPanelProps<T> {
  state: ResourceState<T>
  /** What is missing, in the operator's words - "no orders are ready to pick". */
  emptyMessage: string
  onRetry?: () => void
  children: (data: T) => ReactNode
}

export function AsyncPanel<T>({ state, emptyMessage, onRetry, children }: AsyncPanelProps<T>) {
  if (state.status === 'loading') {
    return (
      <p className="async async-loading" aria-live="polite" aria-busy="true">
        Loading...
      </p>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="async async-error" role="alert">
        <p>{errorCopy(state.errorCode)}</p>
        {onRetry ? (
          <button type="button" className="btn btn-primary thumb-primary pulse-halo" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </div>
    )
  }

  if (state.status === 'empty') {
    return (
      <p className="async async-empty" aria-live="polite">
        {emptyMessage}
      </p>
    )
  }

  return (
    <>
      {state.refreshing ? (
        <p className="async-refreshing" aria-live="polite">
          Refreshing
        </p>
      ) : null}
      {children(state.data as T)}
    </>
  )
}
